import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Lang } from '../../enums/language.enum';
import { DOCUMENT } from '@angular/common';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { HttpBackend } from '@angular/common/http';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { Observable, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LocalizationService {
  private _translate = inject(TranslateService);
  private _localStorage = inject(LocalStorageService);
  private _document = inject(DOCUMENT);

  private _currentLang = signal<Lang>(Lang.English);
  private _isReady = signal<boolean>(false);
  private _isArabic = computed(() => this._currentLang() === Lang.Arabic);
  isArabic = this._isArabic;
  private _isBrowser = typeof window !== 'undefined';

  currentLang = this._currentLang.asReadonly();
  isReady = this._isReady.asReadonly();

  constructor() {
    effect(() => this._adjustApp());
  }

  init(): Observable<any> {
    if (!this._isBrowser) {
      // Avoid rendering SSR content before client-side translations are ready.
      this._isReady.set(false);
      return of(null);
    }
    const selectedLang = (this._localStorage.getItem('userLang') as Lang) || Lang.English;
    this._currentLang.set(selectedLang);
    this._translate.setDefaultLang(Lang.English);
    return this._translate.use(selectedLang).pipe(
      tap(() => {
        this._adjustApp();
        this._isReady.set(true);
      })
    );
  }

  changeLang(): void {
    this._currentLang.set(this._currentLang() === Lang.English ? Lang.Arabic : Lang.English);
    this._localStorage.setItem('userLang', this._currentLang());
    this._isReady.set(false);
    this._translate.use(this._currentLang()).subscribe({
      next: () => {
        this._adjustApp();
        this._isReady.set(true);
      },
      error: () => {
        this._isReady.set(true);
      },
    });
  }

  private _adjustApp(): void {
    const htmlElement = this._document.documentElement;

    if (this._isArabic()) {
      htmlElement.setAttribute('dir', 'rtl');
      htmlElement.setAttribute('lang', 'ar');
    } else {
      htmlElement.setAttribute('dir', 'ltr');
      htmlElement.setAttribute('lang', this._currentLang());
    }
  }

  getTransValue(value: string): string {
    return this._translate.instant(value);
  }

  /**
   * Factory function for loading translation files.
   * Always loads the global '/i18n/' path, plus any additional feature-specific paths.
   *
   * Use in TranslateModule.forRoot() for global config or forChild() for feature modules.
   *
   * @param httpBackend Angular HttpBackend
   * @param additionalPaths Optional additional paths for feature-specific translations (e.g., '/i18n/auth/')
   * @returns MultiTranslateHttpLoader instance
   *
   * @example
   * // In app.config.ts (loads only global translations):
   * useFactory: LocalizationService.createFeatureTranslateLoader,
   * deps: [HttpBackend]
   *
   * @example
   * // In feature route config (loads global + feature translations):
   * useFactory: (httpBackend: HttpBackend) =>
   *   LocalizationService.createFeatureTranslateLoader(httpBackend, '/i18n/auth/'),
   * deps: [HttpBackend]
   */
  static createFeatureTranslateLoader(
    httpBackend: HttpBackend,
    additionalPaths?: string | string[]
  ): MultiTranslateHttpLoader {
    const resources = [{ prefix: '/i18n/', suffix: '.json' }];

    if (additionalPaths) {
      const additionalPathArray = Array.isArray(additionalPaths)
        ? additionalPaths
        : [additionalPaths];
      additionalPathArray.forEach((path) => {
        resources.push({ prefix: path, suffix: '.json' });
      });
    }

    return new MultiTranslateHttpLoader(httpBackend, resources);
  }
}
